import React from "react";
import moment from "moment";
import Dialog from "../Dialog";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITypography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import MUITable from '@mui/material/Table';
import MUITableBody from '@mui/material/TableBody';
import MUITableCell from '@mui/material/TableCell';
import MUITableContainer from '@mui/material/TableContainer';
import MUITableHead from '@mui/material/TableHead';
import MUITableRow from '@mui/material/TableRow';
import MUIPaper from "@mui/material/Paper";
import MUIBox from "@mui/material/Box";
import MUIStack from "@mui/material/Stack";
import MUIGrid from "@mui/material/Grid";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import MUIAlert from "@mui/material/Alert";
import MUILink from "@mui/material/Link";
import Progress from "../Progress";
import OrderService from "../../Seating/Security/OrderService/orderService";
import Calendar from "../../Utility/Calendar";
import DistributionReason from "../../Utility/Crud/DistributionReason";
import HcpcsCode from "../../Utility/Crud/HcpcsCode";
import MainContext from "../../Context/MainContext/MainContext";

const PPR_REQUEST_QUEUE_ID = 121;
const PPR_ESCALATION_QUEUE_ID = 145;
const PPR_REQUEST_STATUS_TERM_ID = 83;
const PPR_DENIED_STATUS_TERM_ID = 84;
const PPR_ESCALATION_STATUS_TERM_ID = 277;
const PPR_REQUEST_FOLLOWUP_STATUS_TERM_ID = 85;
const PPR_ESCALATION_FOLLOWUP_STATUS_TERM_ID = 278;


export default class DialogUpdateInvoice extends Dialog {
    static contextType = MainContext; // For legacy class components

    isPPRApprovalQueue() {
        return this.props.queue?.id === PPR_REQUEST_QUEUE_ID || this.props.queue?.id === PPR_ESCALATION_QUEUE_ID;
    }

    isPPRDestinationSelected(invoice) {
        return invoice.invoiceFollowupStatusTermId === PPR_REQUEST_FOLLOWUP_STATUS_TERM_ID ||
            invoice.invoiceFollowupStatusTermId === PPR_ESCALATION_FOLLOWUP_STATUS_TERM_ID;
    }

    getQueueFollowupDate(invoice) {
        if (this.isPPRDestinationSelected(invoice) ||
            (this.props.queue?.id === PPR_ESCALATION_QUEUE_ID && invoice.approve === true)) {
            return moment(Calendar.addDays({
                daysToAdd: 1,
                useBusinessDays: true,
            })).startOf("day").toISOString();
        }

        if (this.isPPRApprovalQueue() && invoice.approve === false) {
            return moment(Calendar.addDays({
                daysToAdd: 1,
                useBusinessDays: false,
            })).startOf("day").toISOString();
        }

        if (this.props.queue?.id === 138) { // Credit Balance
            return moment(Calendar.addDays({
                daysToAdd: 14,
                useBusinessDays: false,
            })).startOf("day").toISOString();
        }

        if (this.props.queue?.id === 139) { // Appeals
            return moment(Calendar.addDays({
                daysToAdd: 30,
                useBusinessDays: false,
            })).startOf("day").toISOString();
        }

        return moment(Calendar.addDays({
            daysToAdd: 21,
            useBusinessDays: false,
        })).startOf("day").toISOString();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.open !== this.props.open) {
            this.setState({
                noteText: "",
            })
        }
        if (prevProps.invoices.length !== this.props.invoices.length) {
            this.setState({
                invoices: this.props.invoices
            })
        }
        if (prevProps.orderActivityNotes.length !== this.props.orderActivityNotes.length) {
            this.setState({
                orderActivityNotes: this.props.orderActivityNotes.filter((note) => note.noteTypeTermId === 82) // invoice follow-up note
            })
        }
    }

    /**
    * Render header title of dialog
    */
    renderHeader() {
        return (
            <>
                <MUITypography variant="h6" color="textPrimary">
                    Update Invoice
                </MUITypography>
            </>
        );
    }

    /**
     * Render main content of dialog
     */
    renderContent() {
        if (this.state.invoices && this.state.invoices.length > 0) {
            return (
                <>
                    {this.renderOrderAndBrightreeLinks()}
                    {this.renderInvoiceTable()}
                    {this.state.invoices.some((invoice) =>
                        invoice.brightreeInvoiceStatusTermId === PPR_REQUEST_STATUS_TERM_ID ||
                        invoice.brightreeInvoiceStatusTermId === PPR_DENIED_STATUS_TERM_ID ||
                        invoice.brightreeInvoiceStatusTermId === PPR_ESCALATION_STATUS_TERM_ID
                    ) ? this.renderOrderNotes() : null}
                    {this.renderNoteTextField()}
                    {this.renderNextWorkDateAlert()}
                </>
            );
        } else {
            return (
                <Progress message="Fetching Data..." />
            )
        }
    }

    /**
     * Render brightree and sales pilot links at top of the dialog
     */
    renderOrderAndBrightreeLinks() {
        const invoiceDialogLinkStyles = {
            alignItems: "center",
            display: "inline-flex",
            fontWeight: 700,
            gap: 0.75,
            textDecoration: "none",
        };

        const handleOnClickOpenOrder = (orderId) => {
            OrderService.getOrderActivity(orderId).then((order) => {
                let targetUrl = `/chart/${order.patientId}/order/${order.id}`;
                window.open(targetUrl, "_blank");
            });
        }

        return (
            this.state.invoices && this.state.invoices.length > 0 ? (
                <MUIGrid container spacing={3}>
                    {this.state.invoices[0]["orderActivityId"] ? (
                        <MUIGrid item xs={"auto"}>
                            <MUILink
                                href="#"
                                color="primary"
                                sx={invoiceDialogLinkStyles}
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleOnClickOpenOrder(this.state.invoices[0]["orderActivityId"])
                                }}
                            >
                                {`Order #${this.state.invoices[0]["orderActivityId"]}`}
                                <MUIOpenInNewIcon fontSize="small" />
                            </MUILink>
                        </MUIGrid>
                    ) : null}

                    {this.state.invoices[0]["salesOrderId"] ? (
                        <MUIGrid item xs={"auto"}>
                            <MUILink
                                href={`https://brightree.net/F1/02200/OrbitMedical/OrderEntry/frmSOOrderRO.aspx?SalesOrderKey=${this.state.invoices[0]["salesOrderId"]}`}
                                target="_blank"
                                rel="noreferrer"
                                color="primary"
                                sx={invoiceDialogLinkStyles}
                            >
                                {`Brightree ID #${this.state.invoices[0]["salesOrderId"]}`}
                                <MUIOpenInNewIcon fontSize="small" />
                            </MUILink>
                        </MUIGrid>
                    ) : null}

                </MUIGrid>
            ) : null
        )
    }

    /**
     * Render all invoice data and claim status dropdown
     */
    renderInvoiceTable() {
        const invoiceDialogLinkStyles = {
            alignItems: "center",
            display: "inline-flex",
            fontWeight: 700,
            gap: 0.75,
            textDecoration: "none",
            "&:hover": {
                textDecoration: "underline",
            },
        };

        const getInvoiceFollowupStatusOptions = () => {
            return Object.values(this.context.termsIndexed)
                .filter((term) => term.type === "invoice_followup_status")
                .map((option) => ({
                    title: option.name,
                    value: option.id,
                }))
                .sort((a, b) => a.title.localeCompare(b.title));
        };

        const getInvoiceDistributionReasonOptions = () => {
            return Object.values(this.context.distributionReasonsIndexed)
                .filter((option) => option.distributionReasonTypeTermId === 81)
                .map((option) => ({
                    title: DistributionReason.getDisplayName(option),
                    value: option.id,
                }))
                .sort((a, b) => a.title.localeCompare(b.title));
        };

        const formatToUSCurrency = (value) => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(value);
        }

        const handleUpdateInvoiceChange = (updatedInvoice) => {
            this.setState((prevState) => {
                const updatedInvoices = prevState.invoices.map((invoice) => {
                    if (invoice.id !== updatedInvoice.id) {
                        return invoice;
                    }

                    const originalInvoice = invoice.originalInvoice ?? invoice;

                    return {
                        ...invoice,
                        ...updatedInvoice,
                        originalInvoice,
                        followupDate: updatedInvoice.invoiceFollowupStatusTermId != null || updatedInvoice.approve != null
                            ? this.getQueueFollowupDate(updatedInvoice)
                            : originalInvoice.followupDate,
                    };
                });
                return {
                    invoices: updatedInvoices,
                    invoiceIdForApplyToAll: updatedInvoice.id,
                };
            });
        }

        const handleOnClickApplyToAll = (e, selectedOption) => {
            const updatedInvoices = this.state.invoices.map((invoice) => ({
                ...invoice,
                invoiceFollowupStatusTermId: selectedOption,
                followupDate: this.getQueueFollowupDate({
                    ...invoice,
                    invoiceFollowupStatusTermId: selectedOption,
                }),
            }));

            this.setState({
                invoices: updatedInvoices,
                invoiceIdForApplyToAll: null,
            });
        }

        return (
            <MUITableContainer component={MUIPaper} sx={{ mt: 2 }}>
                <MUITable stickyHeader size="small">
                    <MUITableHead>
                        <MUITableRow>
                            <MUITableCell sx={{ whiteSpace: 'nowrap' }}>Invoice #</MUITableCell>
                            <MUITableCell>DoS</MUITableCell>
                            <MUITableCell>{this.state.invoices?.some(invoice => invoice.denials) ? "Payor/HCPCS" : "Payor"}</MUITableCell>
                            <MUITableCell align="right">Amount</MUITableCell>
                            <MUITableCell>Status</MUITableCell>
                        </MUITableRow>
                    </MUITableHead>
                    <MUITableBody>
                        {this.state.invoices && this.state.invoices.length > 0 ? (
                            this.state.invoices
                                .sort((a, b) => new Date(b.dateOfService) - new Date(a.dateOfService))
                                .map((invoice) => {
                                    const selectedOption = getInvoiceFollowupStatusOptions().find(
                                        (option) => option.value === invoice.invoiceFollowupStatusTermId
                                    );

                                    const selectedInvoiceDistributionReason = invoice.distributionReasonId
                                        ? getInvoiceDistributionReasonOptions().find(
                                            (option) => option.value === invoice.distributionReasonId
                                        )
                                        : null;

                                    return (
                                        <React.Fragment key={invoice.id}>
                                            <MUITableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <MUITableCell>
                                                    {invoice.id && invoice.key ? (
                                                    <MUILink
                                                        href={`https://brightree.net/F1/02200/OrbitMedical/Receipts/Invoices/Invoice_Invoice.aspx?InvoiceKey=${invoice.key}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        color="primary"
                                                        sx={invoiceDialogLinkStyles}
                                                    >
                                                            {invoice.id}
                                                    </MUILink>
                                                    ) : invoice.id}
                                                </MUITableCell>
                                                <MUITableCell>{invoice.dateOfService ? moment(invoice.dateOfService).format("M/D/YYYY") : null}</MUITableCell>
                                                <MUITableCell >{invoice.payorName ? invoice.payorName : null}</MUITableCell>
                                                <MUITableCell align="right">{invoice.balance ? formatToUSCurrency(invoice.balance) : formatToUSCurrency(0)}</MUITableCell>
                                                <MUITableCell sx={{ width: "330px" }}>
                                                    <MUIStack direction="row" alignItems="center" spacing={1}>
                                                        <MUIBox sx={{ flexGrow: 1 }}>
                                                            {!this.isPPRApprovalQueue() ?
                                                                (
                                                                    <MUIStack direction="column" spacing={1}>
                                                                        <MUIAutocomplete
                                                                            options={getInvoiceFollowupStatusOptions()}
                                                                            value={selectedOption || null}
                                                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                            getOptionLabel={(option) => option.title}
                                                                            onChange={(e, newValue) => {
                                                                                handleUpdateInvoiceChange({
                                                                                    ...invoice,
                                                                                    invoiceFollowupStatusTermId: newValue ? newValue.value : null,
                                                                                });
                                                                            }}
                                                                            renderInput={(params) => {
                                                                                return (
                                                                                    <MUITextField
                                                                                        {...params}
                                                                                        InputLabelProps={{ shrink: true }}
                                                                                        size="small"
                                                                                        variant="standard"
                                                                                        fullWidth={true}
                                                                                    />
                                                                                );
                                                                            }}
                                                                        />
                                                                        {this.isPPRDestinationSelected(invoice) ? (
                                                                            <MUIBox>
                                                                                <MUIAutocomplete
                                                                                    options={getInvoiceDistributionReasonOptions()}
                                                                                    value={selectedInvoiceDistributionReason || null}
                                                                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                                    getOptionLabel={(option) => option.title}
                                                                                    onChange={(e, newValue) => {
                                                                                        handleUpdateInvoiceChange({
                                                                                            ...invoice,
                                                                                            distributionReasonId: newValue ? newValue.value : null,
                                                                                        });
                                                                                    }}
                                                                                    renderInput={(params) => {
                                                                                        return (
                                                                                            <MUITextField
                                                                                                {...params}
                                                                                                InputLabelProps={{ shrink: true }}
                                                                                                size="small"
                                                                                                variant="standard"
                                                                                                fullWidth={true}
                                                                                            />
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </MUIBox>
                                                                        ) : null}
                                                                    </MUIStack>
                                                                ) : (
                                                                    <MUIAutocomplete
                                                                        options={[
                                                                            { title: "Approve", value: true },
                                                                            { title: "Deny", value: false },
                                                                        ]}
                                                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                        getOptionLabel={(option) => option.title}
                                                                        onChange={(e, newValue) => {
                                                                            handleUpdateInvoiceChange({
                                                                                ...invoice,
                                                                                approve: newValue ? newValue.value : null,
                                                                            });
                                                                        }}
                                                                        renderInput={(params) => {
                                                                            return (
                                                                                <MUITextField
                                                                                    {...params}
                                                                                    InputLabelProps={{ shrink: true }}
                                                                                    size="small"
                                                                                    variant="standard"
                                                                                    fullWidth={true}
                                                                                />
                                                                            );
                                                                        }}
                                                                    />
                                                                )}
                                                        </MUIBox>
                                                        {this.state.invoices.length > 1 && selectedOption && !this.isPPRDestinationSelected(invoice) && this.state.invoiceIdForApplyToAll === invoice.id ? (
                                                            <MUIBox>
                                                                <MUIButton size="small" onClick={(e) => handleOnClickApplyToAll(e, selectedOption.value)}>Apply to all</MUIButton>
                                                            </MUIBox>
                                                        ) : null}
                                                    </MUIStack>
                                                </MUITableCell>
                                            </MUITableRow>
                                            {invoice.denials && invoice.denials.length > 0 ? (
                                                invoice.denials
                                                    .sort((a, b) => {
                                                        const dateA = new Date(a.created_at);
                                                        const dateB = new Date(b.created_at);
                                                        if (dateB > dateA) return 1;
                                                        if (dateB < dateA) return -1;

                                                        const hcpcsA = this.context.hcpcsCodesIndexed[a.hcpcsCodeId]?.code || '';
                                                        const hcpcsB = this.context.hcpcsCodesIndexed[b.hcpcsCodeId]?.code || '';

                                                        if (hcpcsA < hcpcsB) return -1;
                                                        if (hcpcsA > hcpcsB) return 1;
                                                        return 0;
                                                    })
                                                    .map((denial) => {
                                                        return (
                                                            <MUITableRow sx={{ backgroundColor: (theme) => theme.palette.background.default }} key={denial.id}>
                                                                <MUITableCell></MUITableCell>
                                                                <MUITableCell>{denial.created_at ? moment(denial.created_at).format("M/D/YYYY") : null}</MUITableCell>
                                                                <MUITableCell>{denial.hcpcsCodeId ? HcpcsCode.getDisplayName(this.context.hcpcsCodesIndexed[denial.hcpcsCodeId]) : null}</MUITableCell>
                                                                <MUITableCell align="right" sx={{ color: denial.amount < 0 ? (theme) => theme.palette.error.main : "" }}>
                                                                    {denial.amount ? denial.amount < 0 ? `(${formatToUSCurrency(denial.amount * -1)})` : formatToUSCurrency(denial.amount) : formatToUSCurrency(0)}
                                                                </MUITableCell>
                                                                <MUITableCell component="th" scope="row" style={{ display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: '300px' }} title={`${this.context.distributionReasonsIndexed[denial.distributionReasonId].code} - ${DistributionReason.getDisplayName(this.context.distributionReasonsIndexed[denial.distributionReasonId])}`}>
                                                                    {denial.distributionReasonId ? `${this.context.distributionReasonsIndexed[denial.distributionReasonId].code} - ${DistributionReason.getDisplayName(this.context.distributionReasonsIndexed[denial.distributionReasonId])}` : null}
                                                                </MUITableCell>
                                                            </MUITableRow>
                                                        );
                                                    })
                                            ) : null}
                                        </React.Fragment>
                                    )
                                })
                        ) : null}
                    </MUITableBody>
                </MUITable>
            </MUITableContainer>
        )
    }

    renderOrderNotes() {
        if (this.state.orderActivityNotes && this.state.orderActivityNotes.length > 0) {
            this.state.orderActivityNotes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            return (
                <MUIBox sx={{ mt: 3 }}>
                    <MUIGrid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ marginBottom: 1 }}
                    >
                        <MUIGrid item sx={{ paddingBottom: 0 }}>
                            <MUITypography variant="subtitle1" sx={{ fontWeight: 500, display: "block" }}>
                                PPR Request Notes
                            </MUITypography>
                        </MUIGrid>
                        <MUIGrid item>
                            <MUITypography
                                variant="caption"
                                color="text.primary"
                                sx={{
                                    backgroundColor: "background.default",
                                    borderRadius: 999,
                                    display: "inline-flex",
                                    fontWeight: 600,
                                    paddingX: 1.25,
                                    paddingY: 0.5,
                                }}
                            >
                                {`${this.state.orderActivityNotes.length} ${this.state.orderActivityNotes.length === 1 ? "Note" : "Notes"}`}
                            </MUITypography>
                        </MUIGrid>
                    </MUIGrid>
                    <MUITableContainer
                        sx={{
                            maxHeight: 300,
                            border: 1,
                            borderColor: "divider",
                        }}>
                        <MUITable stickyHeader
                            size="small">
                            <MUITableBody
                            sx={{
                                "& .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root": {
                                    backgroundColor: "background.default",
                                },
                                "& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root": {
                                    backgroundColor: "background.paper",
                                },
                            }}>
                                {this.state.orderActivityNotes.map((orderActivityNote, orderActivityNoteIndex) => {
                                    const createdByDisplayName = orderActivityNote.createdBy
                                        ? `${orderActivityNote.createdBy.firstname} ${orderActivityNote.createdBy.lastname}`
                                        : "Unknown";

                                    return (
                                        <MUITableRow key={orderActivityNote.id ?? orderActivityNoteIndex}>
                                            <MUITableCell
                                                sx={{
                                                    maxWidth: "180px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    verticalAlign: "top",
                                                    whiteSpace: "nowrap",
                                                    width: "180px",
                                                }}
                                            >
                                                {moment(orderActivityNote.createdAt).format("M/D/YYYY h:mma")}
                                                <MUITypography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    component="div"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {createdByDisplayName}
                                                </MUITypography>
                                            </MUITableCell>
                                            <MUITableCell>{orderActivityNote.text}</MUITableCell>
                                        </MUITableRow>
                                    );
                                })}
                            </MUITableBody>
                        </MUITable>
                    </MUITableContainer>
                </MUIBox>
            );
        }
        return null;
    }

    /**
     * Render textfield and alert for note section
     */
    renderNoteTextField() {
        const handleNoteTextChange = (updatedFields) => {
            this.setState({
                noteText: updatedFields.noteText
            });
        };

        return (
            <MUIBox sx={{ mt: 2 }}>
                <MUITextField
                    multiline
                    rows={3}
                    disabled={this.state.invoices && this.state.invoices.length > 0 && !this.state.invoices.some((invoice) => invoice.orderActivityId)}
                    fullWidth={true}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { resize: "both" } }}
                    label={"Note"}
                    value={this.state.noteText ? this.state.noteText : ""}
                    onChange={(e) => handleNoteTextChange({ noteText: e.target.value })}
                    variant="standard"
                    required={this.state.invoices?.some((invoice) => this.isPPRDestinationSelected(invoice))}
                />
                {this.state.invoices && this.state.invoices.length > 0 && !this.state.invoices.some((invoice) => invoice.orderActivityId) ? (
                    <MUIAlert severity="warning" sx={{ mt: 1 }}>
                        Cannot add note to invoices that do not have a Sales Pilot Order #.
                    </MUIAlert>
                ) : null}
            </MUIBox>
        )
    }

    renderNextWorkDateAlert() {
        if (!this.state.invoices || this.state.invoices.length === 0) {
            return null;
        }

        const today = moment().startOf("day");
        const followupDates = [];
        const seenFollowupDates = new Set();

        this.state.invoices.forEach((invoice) => {
            if (!invoice.followupDate) {
                return;
            }

            const followupDate = moment(invoice.followupDate).startOf("day");

            if (!followupDate.isAfter(today, "day")) {
                return;
            }

            const formattedFollowupDate = followupDate.format("YYYY-MM-DD");

            if (seenFollowupDates.has(formattedFollowupDate)) {
                return;
            }

            seenFollowupDates.add(formattedFollowupDate);
            followupDates.push({
                date: formattedFollowupDate,
                daysAway: followupDate.diff(today, "days"),
            });
        });

        if (followupDates.length === 0) {
            return null;
        }

        const formattedFollowupDates = followupDates.map(({ date, daysAway }) => {
            const dayLabel = daysAway === 1 ? "day" : "days";
            return `${moment(date, "YYYY-MM-DD").format("MMMM D, YYYY")} (${daysAway} ${dayLabel})`;
        });
        const label = formattedFollowupDates.length === 1 ? "Next followup date" : "Followup dates";

        return (
            <MUIAlert severity="info" sx={{ mt: 2 }}>
                {`${label}: ${formattedFollowupDates.join(", ")}`}
            </MUIAlert>
        );
    }

    /**
     * Render cancel and confirm button actions of dialog.
     */
    renderActions() {
        const isDisabled = () => {
            const isPPRDestinationSelected = this.state.invoices?.some((invoice) => this.isPPRDestinationSelected(invoice));

            const allPPRHaveDistributionReason = this.state.invoices
                ?.filter((invoice) => this.isPPRDestinationSelected(invoice))
                .every((invoice) => invoice.distributionReasonId != null);

            const isPPRApprovalQueue = this.isPPRApprovalQueue();

            const isAnyFollowupStatusSelected = this.state.invoices?.some((invoice) => isPPRApprovalQueue
                ? invoice.approve === true || invoice.approve === false
                : invoice.invoiceFollowupStatusTermId != null);

            const isNoteTextProvided = this.state.noteText && this.state.noteText.trim() !== '';

            return !(isPPRDestinationSelected
                ? isAnyFollowupStatusSelected && isNoteTextProvided && allPPRHaveDistributionReason
                : isAnyFollowupStatusSelected || isNoteTextProvided);
        };

        const hasPPRDenial = this.state.invoices?.some((invoice) =>
            invoice.brightreeInvoiceStatusTermId === PPR_DENIED_STATUS_TERM_ID
        );

        return (
            <MUIDialogActions spacing={2}>
                <MUIButton variant="text" onClick={this.props.onClose}>
                    Cancel
                </MUIButton>
                {hasPPRDenial ? (
                    <MUILoadingButton
                        variant="outlined"
                        loading={this.props.loading}
                        onClick={() => this.props.onClosePPRDenial(this.state.invoices)}>
                        Close PPR Denial
                    </MUILoadingButton>
                ) : null}
                <MUILoadingButton
                    variant="contained"
                    disabled={isDisabled()}
                    loading={this.props.loading}
                    onClick={() => this.props.onConfirm(this.state.invoices, this.state.noteText)}>
                    Update Invoice
                </MUILoadingButton>
            </MUIDialogActions>
        );
    }

    /**
     * @returns {string} The max width of the dialog.
     */
    getMaxWidth() {
        return "md";
    }
}
